/**
 * PurchaseOrdersPage — PO listing with interactive column features:
 *   1. Drag-resize columns via dividers between headers
 *   2. Column picker popover to show/hide columns
 *   3. Drag-to-reorder column headers
 *   4. Horizontal scrolling for wide tables
 *   Column preferences are persisted to backend.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { PageHeader, DataTableToolbar, PaginationBar, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { sortData } from '../components/SortableTableHeader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useFormatter } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import {
  Plus, ChevronDown, ChevronUp, ChevronsUpDown, FileText, BarChart3,
  Settings2, Eye, EyeOff, RotateCcw, GripVertical, Maximize2,
} from 'lucide-react';
import ModuleReports from './Reports/ModuleReports';
import { PO_REPORTS } from './Reports/reportConfigs';

const STATUS_COLORS = {
  UNISSUED: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  ACKNOWLEDGED: 'bg-indigo-100 text-indigo-700',
  AMENDED: 'bg-orange-100 text-orange-700',
  RECEIVED: 'bg-teal-100 text-teal-700',
  PARTIAL: 'bg-cyan-100 text-cyan-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  CLOSED: 'bg-slate-100 text-slate-700',
};

function POStatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
  return <Badge className={`${cls} text-[10px] font-semibold inline-flex justify-center min-w-[88px]`}>{status}</Badge>;
}

import PODashboard from './PO/PODashboard';

export default function PurchaseOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  return (
    <AppShell>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Purchase Orders</h1>
          <HelpButton guideId="purchase-orders" label="Purchase Orders Guide" />
        </div>
      </div>
      <div className="flex gap-1 border-b mb-4" data-testid="po-tabs">
        {[{ id: 'dashboard', label: 'Analytics', icon: BarChart3 }, { id: 'orders', label: 'Orders' }, { id: 'reports', label: 'Reports', icon: FileText }].map(t => (
          <button key={t.id} onClick={() => setSearchParams({ tab: t.id })} data-testid={`po-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'orders' && <POListContent />}
      {activeTab === 'dashboard' && <PODashboard />}
      {activeTab === 'reports' && <ModuleReports reports={PO_REPORTS} apiPrefix="/api/module-reports/orders" moduleLabel="Purchase Orders" />}
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  COLUMN DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════

const buildDefaultColumns = (currency) => [
  { key: 'order_number', label: 'Order Number', width: 140, sortField: 'order_number', visible: true },
  { key: 'status', label: 'Order Status', width: 130, sortField: 'status', visible: true },
  { key: 'net_total', label: `Order Net Value (${currency})`, width: 170, sortField: 'net_total', align: 'right', visible: true },
  { key: 'order_date', label: 'Order Date', width: 130, sortField: 'order_date', visible: true },
  { key: 'supplier_name', label: 'Supplier', width: 200, sortField: 'supplier_name', visible: true },
  { key: 'description', label: 'Description', width: 250, sortField: 'description', visible: true },
  { key: 'request_number', label: 'Request Number', width: 140, sortField: 'request_number', visible: true },
  { key: 'buyer_name', label: 'Buyer', width: 150, sortField: 'buyer_name', visible: false },
  { key: 'document_type_name', label: 'Document Type', width: 150, sortField: 'document_type_name', visible: false },
  { key: 'delivery_point_code', label: 'Delivery Code', width: 130, sortField: 'delivery_point_code', visible: false },
  { key: 'currency', label: 'Currency', width: 90, sortField: 'currency', visible: false },
  { key: 'ref_num', label: 'Ref Number', width: 120, sortField: 'ref_num', visible: false },
  { key: 'created_at', label: 'Created Date', width: 130, sortField: 'created_at', visible: false },
];

const PREF_MODULE = 'po-listing';
const PREF_KEY = 'po-listing_main';

// ═══════════════════════════════════════════════════════════════════════════
//  PO LIST CONTENT
// ═══════════════════════════════════════════════════════════════════════════

function POListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fmt, systemCurrency } = useFormatter();
  const { systemSettings } = useAuth();
  const currency = systemSettings?.system_currency || 'USD';
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [overdueFilter, setOverdueFilter] = useState(searchParams.get('overdue') || '');
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get('supplier') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [loading, setLoading] = useState(false);
  const [sortState, setSortState] = useState({ field: 'order_number', dir: 'desc' });

  // Sync filter state when URL searchParams change (dashboard drill-down)
  useEffect(() => {
    const sp_status = searchParams.get('status') || '';
    const sp_search = searchParams.get('search') || '';
    const sp_overdue = searchParams.get('overdue') || '';
    const sp_supplier = searchParams.get('supplier') || '';
    const sp_category = searchParams.get('category') || '';
    if (sp_status !== statusFilter) setStatusFilter(sp_status);
    if (sp_search !== search) setSearch(sp_search);
    if (sp_overdue !== overdueFilter) setOverdueFilter(sp_overdue);
    if (sp_supplier !== supplierFilter) setSupplierFilter(sp_supplier);
    if (sp_category !== categoryFilter) setCategoryFilter(sp_category);
    setPage(1);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDashboardFilter = !!(searchParams.get('status') || searchParams.get('overdue') || searchParams.get('supplier') || searchParams.get('category') || searchParams.get('search'));
  const filterLabel = searchParams.get('label') || '';

  const clearDashboardFilters = () => {
    setStatusFilter(''); setOverdueFilter(''); setSupplierFilter(''); setCategoryFilter(''); setSearch('');
    setSearchParams({ tab: 'orders' });
  };

  // Column state
  const defaults = useMemo(() => buildDefaultColumns(currency), [currency]);
  const [columns, setColumns] = useState(defaults);
  const [colsLoaded, setColsLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Load column prefs from backend
  useEffect(() => {
    api.get(`/api/saved-reports/column-prefs/${PREF_MODULE}/${PREF_KEY}`).then(r => {
      if (r.data?.columns?.length) {
        const savedMap = {};
        r.data.columns.forEach((sc, i) => { savedMap[sc.key] = { ...sc, _order: i }; });
        const merged = [];
        const savedKeys = r.data.columns.map(sc => sc.key);
        for (const sk of savedKeys) {
          const def = defaults.find(d => d.key === sk);
          if (def) {
            const saved = savedMap[sk];
            merged.push({ ...def, visible: saved.visible !== false, width: saved.width || def.width });
          }
        }
        for (const dc of defaults) {
          if (!savedKeys.includes(dc.key)) merged.push({ ...dc });
        }
        setColumns(merged);
      }
      setColsLoaded(true);
    }).catch(() => setColsLoaded(true));
  }, [defaults]);

  // Save column prefs to backend
  const savePrefs = useCallback((cols) => {
    const toSave = cols.map(c => ({ key: c.key, visible: c.visible, width: c.width }));
    api.put(`/api/saved-reports/column-prefs/${PREF_MODULE}/${PREF_KEY}`, { columns: toSave }).catch(() => {});
  }, []);

  const visibleCols = useMemo(() => columns.filter(c => c.visible), [columns]);

  // ── Data loading ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, page_size: pageSize, status: statusFilter, sort_by: sortState.field, sort_dir: sortState.dir };
      if (overdueFilter) params.overdue = 'true';
      if (supplierFilter) params.supplier = supplierFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get('/api/orders', { params });
      setRecords(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { if (e?.response?.status >= 500) toast.error('Failed to load purchase orders'); else if (!e?.response) toast.error('Connection error — please refresh the page'); }
    setLoading(false);
  }, [search, page, pageSize, statusFilter, sortState, overdueFilter, supplierFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Create PO ──
  const [creating, setCreating] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);

  useEffect(() => {
    const h = e => { if (createMenuRef.current && !createMenuRef.current.contains(e.target)) setCreateMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const createPO = async (type) => {
    setCreating(true); setCreateMenuOpen(false);
    try {
      const res = await api.post('/api/orders/create', { order_type: type });
      toast.success(res.data.message);
      navigate(`/orders/${res.data.order_number}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create order')); }
    setCreating(false);
  };

  // ── Sort handler ──
  const handleSort = useCallback((field) => {
    setSortState(prev => {
      if (prev.field === field) return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { field, dir: 'asc' };
    });
  }, []);

  // ── Column resize ──
  const resizeRef = useRef({ active: false, colIdx: -1, startX: 0, startW: 0 });

  const onResizeStart = useCallback((e, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const col = visibleCols[colIdx];
    resizeRef.current = { active: true, colIdx, startX: e.clientX, startW: col.width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [visibleCols]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeRef.current.active) return;
      const { colIdx, startX, startW } = resizeRef.current;
      const delta = e.clientX - startX;
      const newWidth = Math.max(50, startW + delta);
      setColumns(prev => {
        const colKey = visibleCols[colIdx]?.key;
        if (!colKey) return prev;
        return prev.map(c => c.key === colKey ? { ...c, width: newWidth } : c);
      });
    };
    const onMouseUp = () => {
      if (resizeRef.current.active) {
        resizeRef.current.active = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setColumns(prev => { savePrefs(prev); return prev; });
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [visibleCols, savePrefs]);

  // ── Fit to Screen ──
  const tableContainerRef = useRef(null);
  const fitToScreen = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const availableWidth = container.clientWidth;
    const vis = columns.filter(c => c.visible);
    const totalCurrent = vis.reduce((s, c) => s + c.width, 0);
    if (totalCurrent === 0 || availableWidth === 0) return;
    const ratio = availableWidth / totalCurrent;
    setColumns(prev => {
      const updated = prev.map(c => c.visible ? { ...c, width: Math.max(50, Math.round(c.width * ratio)) } : c);
      savePrefs(updated);
      return updated;
    });
  }, [columns, savePrefs]);

  // ── Column drag-reorder ──
  const dragRef = useRef({ dragging: false, fromKey: null });
  const [dropTarget, setDropTarget] = useState(null);

  const onDragStart = useCallback((e, colKey) => {
    if (columns.find(c => c.key === colKey)?.fixed) { e.preventDefault(); return; }
    dragRef.current = { dragging: true, fromKey: colKey };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    // Semi-transparent drag image
    e.currentTarget.style.opacity = '0.5';
  }, [columns]);

  const onDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = '1';
    dragRef.current.dragging = false;
    setDropTarget(null);
  }, []);

  const onDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragRef.current.fromKey !== colKey) setDropTarget(colKey);
  }, []);

  const onDrop = useCallback((e, toKey) => {
    e.preventDefault();
    const fromKey = dragRef.current.fromKey;
    if (!fromKey || fromKey === toKey) { setDropTarget(null); return; }
    setColumns(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c.key === fromKey);
      const toIdx = arr.findIndex(c => c.key === toKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      savePrefs(arr);
      return arr;
    });
    setDropTarget(null);
  }, [savePrefs]);

  // ── Column picker ──
  const toggleColVisibility = useCallback((key) => {
    setColumns(prev => {
      const updated = prev.map(c => c.key === key ? { ...c, visible: !c.visible } : c);
      savePrefs(updated);
      return updated;
    });
  }, [savePrefs]);

  const resetColumns = useCallback(() => {
    const fresh = buildDefaultColumns(currency);
    setColumns(fresh);
    savePrefs(fresh);
  }, [currency, savePrefs]);

  // ── Cell renderer ──
  const renderCell = useCallback((col, r) => {
    switch (col.key) {
      case 'order_number': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.order_number}</span>;
      case 'status': return <POStatusBadge status={r.status} />;
      case 'net_total': return <span className="tabular-nums text-sm">{fmt(r.net_total || r.total_value)}</span>;
      case 'order_date': return <span className="text-sm whitespace-nowrap">{r.order_date ? new Date(r.order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}</span>;
      case 'supplier_name': return <span className="text-sm">{r.supplier_name || r.supplier_code || '—'}</span>;
      case 'description': return <span className="text-sm block truncate">{r.description || '—'}</span>;
      case 'request_number': return <span className="font-mono text-sm text-primary">{r.request_number || '—'}</span>;
      case 'created_at': return <span className="text-sm whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}</span>;
      default: return <span className="text-sm">{r[col.key] !== undefined && r[col.key] !== null ? String(r[col.key]) : '—'}</span>;
    }
  }, [fmt]);

  // ── Picker drag-reorder (inside picker panel) ──
  const [pickerDragIdx, setPickerDragIdx] = useState(null);
  const onPickerDragStart = (idx) => setPickerDragIdx(idx);
  const onPickerDragOver = (e, idx) => {
    e.preventDefault();
    if (pickerDragIdx === null || pickerDragIdx === idx) return;
    setColumns(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(pickerDragIdx, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setPickerDragIdx(idx);
  };
  const onPickerDragEnd = () => {
    setPickerDragIdx(null);
    setColumns(prev => { savePrefs(prev); return prev; });
  };

  return (
    <>
      <PageHeader description={`Total records: ${total}`}
        actions={
          <div className="relative" ref={createMenuRef}>
            <Button onClick={() => setCreateMenuOpen(!createMenuOpen)} disabled={creating} data-testid="add-order-btn">
              <Plus className="h-4 w-4 mr-1" /> {creating ? 'Creating...' : 'Create Purchase Order'} <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
            {createMenuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-card border rounded-lg shadow-xl z-50 w-64 py-1">
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-accent flex items-center gap-2" onClick={() => createPO('standard')}>
                  <Plus className="h-4 w-4 text-primary" /> Create New Order
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-accent flex items-center gap-2" onClick={() => createPO('from_quote')}>
                  <FileText className="h-4 w-4 text-green-600" /> Create from Quote / Invoice
                </button>
              </div>
            )}
          </div>
        } />

      <div className="flex items-center gap-3 mb-3">
        {hasDashboardFilter && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 mr-2">
            <BarChart3 className="h-3.5 w-3.5" />
            <span className="font-medium">Filtered{filterLabel ? `: ${filterLabel}` : ''}</span>
            <button onClick={clearDashboardFilters} className="ml-1 hover:text-blue-900 font-bold" data-testid="clear-dashboard-filter">&times;</button>
          </div>
        )}
        <DataTableToolbar searchValue={search} onSearch={setSearch} searchPlaceholder="Search orders..." />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm" data-testid="status-filter">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Column tools */}
        <div className="relative ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={fitToScreen}
            className="h-9 px-3 gap-1.5" data-testid="fit-to-screen-btn" title="Fit columns to screen width">
            <Maximize2 className="h-3.5 w-3.5" />
            <span className="text-xs">Fit to Screen</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(!pickerOpen)}
            className="h-9 px-3 gap-1.5" data-testid="column-picker-btn" title="Configure columns">
            <Settings2 className="h-3.5 w-3.5" />
            <span className="text-xs">Columns</span>
          </Button>
          {pickerOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
              <div className="absolute right-0 top-10 z-50 w-80 bg-white border rounded-xl shadow-2xl p-3 max-h-[450px] overflow-auto"
                data-testid="column-picker-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-800">Configure Columns</span>
                  <button onClick={resetColumns} className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                    data-testid="column-picker-reset">
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">Toggle visibility. Drag to reorder.</p>
                <div className="space-y-1">
                  {columns.filter(c => !c.fixed).map((c, i) => {
                    const realIdx = columns.indexOf(c);
                    return (
                      <div key={c.key}
                        draggable
                        onDragStart={() => onPickerDragStart(realIdx)}
                        onDragOver={(e) => onPickerDragOver(e, realIdx)}
                        onDragEnd={onPickerDragEnd}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-colors ${
                          c.visible ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                        } ${pickerDragIdx === realIdx ? 'ring-2 ring-primary/30' : ''}`}
                        data-testid={`column-picker-item-${c.key}`}>
                        <GripVertical className="h-3 w-3 text-slate-400 cursor-grab flex-shrink-0" />
                        <button onClick={() => toggleColVisibility(c.key)} className="flex-shrink-0"
                          data-testid={`column-toggle-${c.key}`}>
                          {c.visible
                            ? <Eye className="h-3.5 w-3.5 text-emerald-600" />
                            : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                        </button>
                        <span className={`flex-1 truncate ${c.visible ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{c.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : records.length === 0 ? (
        <EmptyState title="No purchase orders found" />
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="po-table-container" ref={tableContainerRef}>
          <table className="text-sm" style={{ tableLayout: 'fixed', width: visibleCols.reduce((s, c) => s + c.width, 0) }}
            aria-label="Purchase Orders list" data-testid="po-interactive-table">
            <thead>
              <tr className="border-b bg-muted/50">
                {visibleCols.map((col, ci) => {
                  const isActive = sortState.field === col.sortField;
                  const dir = isActive ? sortState.dir : null;
                  const isDragTarget = dropTarget === col.key;
                  return (
                    <th key={col.key}
                      style={{ width: col.width, minWidth: 50, position: 'relative' }}
                      className={`h-10 px-3 text-left align-middle font-semibold text-[11px] uppercase tracking-wide text-muted-foreground select-none whitespace-nowrap ${
                        col.align === 'right' ? 'text-right' : ''
                      } ${isDragTarget ? 'bg-primary/10 border-l-2 border-primary' : ''}`}
                      draggable={!col.fixed}
                      onDragStart={(e) => onDragStart(e, col.key)}
                      onDragEnd={onDragEnd}
                      onDragOver={(e) => onDragOver(e, col.key)}
                      onDrop={(e) => onDrop(e, col.key)}
                      data-testid={`col-header-${col.key}`}>
                      {col.sortable !== false ? (
                        <button
                          onClick={() => handleSort(col.sortField)}
                          className={`flex items-center gap-1 hover:text-foreground transition-colors ${isActive ? 'text-primary' : ''} ${col.align === 'right' ? 'ml-auto' : ''}`}>
                          <span>{col.label}</span>
                          <span className="flex-shrink-0">
                            {!isActive && <ChevronsUpDown className="h-3 w-3 opacity-40" />}
                            {isActive && dir === 'asc' && <ChevronUp className="h-3 w-3" />}
                            {isActive && dir === 'desc' && <ChevronDown className="h-3 w-3" />}
                          </span>
                        </button>
                      ) : <span>{col.label}</span>}
                      {/* Resize handle */}
                      {ci < visibleCols.length - 1 && (
                        <div
                          className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-10 group"
                          onMouseDown={(e) => onResizeStart(e, ci)}
                          data-testid={`resize-handle-${col.key}`}>
                          <div className="absolute right-[2px] top-2 bottom-2 w-[1px] bg-border group-hover:bg-primary/60 transition-colors" />
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.order_number}
                  className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/orders/${r.order_number}`)}
                  data-testid="po-data-row">
                  {visibleCols.map(col => (
                    <td key={col.key}
                      style={{ width: col.width, maxWidth: col.width, overflow: 'hidden' }}
                      className={`p-2 align-middle ${col.align === 'right' ? 'text-right' : ''}`}>
                      {renderCell(col, r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </>
  );
}
